import { WarningOutlined } from '@ant-design/icons'
import { Alert, Card, Flex, Modal, Switch, Tag } from 'antd'
import dayjs from 'dayjs'
import { BoosterProgramStatus } from 'Modules'
import { useState } from 'react'

import { BOOSTER_PROGRAM_STATUS_CONFIG } from 'constants/booster-program'

interface Props {
  status: BoosterProgramStatus
  isActive: boolean
  message: string
  pausedAt?: string
  pausedBy?: string
  onActivate?: () => void
  onPause?: () => void
}

export const BoosterProgramStatusCard = ({
  status,
  isActive,
  message,
  pausedAt,
  pausedBy,
  onActivate,
  onPause,
}: Props) => {
  const [confirmOpen, setConfirmOpen] = useState(false)

  const config = BOOSTER_PROGRAM_STATUS_CONFIG[status]
  const canToggle = !!(onActivate || onPause)
  const switchEnabled = canToggle && (status === 'ACTIVE' || status === 'PAUSED')
  const isPaused = status === 'PAUSED'

  const handleSwitchChange = (checked: boolean) => {
    if (!checked) {
      setConfirmOpen(true)
      return
    }
    onActivate?.()
  }

  const confirmPause = () => {
    onPause?.()
    setConfirmOpen(false)
  }

  return (
    <Card className="booster-detail__status-card">
      <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
        <Flex align="center" gap={12}>
          <span className="booster-detail__status-title">Program Status</span>
          <Tag
            className="booster-detail__status-tag"
            style={{
              color: config.color,
              backgroundColor: config.background,
              borderColor: 'transparent',
            }}
          >
            {config.label}
          </Tag>
        </Flex>
      </Flex>

      <Flex justify="space-between" align="center" className="booster-detail__status-row">
        <span className="booster-detail__status-label">Program Activation</span>
        <Switch checked={isActive} disabled={!switchEnabled} onChange={handleSwitchChange} />
      </Flex>

      {isPaused ? (
        <Alert
          type="warning"
          icon={<WarningOutlined />}
          showIcon
          message={message}
          className="booster-detail__status-alert"
        />
      ) : (
        <div className="booster-detail__status-message">{message}</div>
      )}

      {isPaused && pausedAt && (
        <div className="booster-detail__paused-info">
          Paused on {dayjs(pausedAt).format('MMM D, YYYY')}
          {pausedBy ? ` by ${pausedBy}` : ''}
        </div>
      )}

      <Modal
        title="Pause Booster Program?"
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onOk={confirmPause}
        okText="Yes, Pause Program"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>Milestone tracking will stop immediately.</p>
        <p>No new promo codes will be issued.</p>
        <p>
          Users&apos; enrollment periods are also paused and will resume from where they left off
          when the program is turned back on.
        </p>
        <p>Already issued codes remain valid.</p>
      </Modal>
    </Card>
  )
}
